import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clone, concat, findIndex, isEmpty, move, propEq, remove } from 'ramda';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Settings from '@components/@core/Settings';
import Thumbnail from '@components/@core/Thumbnail';
import Toggle from '@components/@core/Toggle';
import AppActions from '~/actions/app';
import Course from './Course';

const Add = ({ onUpdate }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [objectives, setObjectives] = useState(['', '']);
    const [selected, setSelected] = useState({
        author: [],
        department: [],
        competency: [],
        category: [],
    });
    const [chronologicalLock, setChronologicalLock] = useState(false);
    const [timeLock, setTimeLock] = useState(false);
    const [courses, setCourses] = useState([]);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleAttachCourse = () => {
        dispatch(
            AppActions.modalRequest({
                type: 'Attach Library',
                data: { before: { show: ['courses'], selected: courses } },
                callBack: {
                    onAttach: (e) => {
                        const _courses = concat(courses, e.templates);
                        setCourses(_courses);
                    },
                },
            })
        );
    };
    const handleChildMenu = (event, item) => {
        const index = findIndex(propEq('id', item.id), courses);
        if (index > -1) {
            let _courses = clone(courses);
            if (event === 'move up') {
                if (index === 0) return;
                _courses = move(index, index - 1, _courses);
            } else if (event === 'move down') {
                if (_courses?.length === index + 1) return;
                _courses = move(index, index + 1, _courses);
            } else if (event === 'remove') {
                _courses = remove(index, 1, _courses);
            } else if (event === 'detail view') {
                navigate({ pathname: `/library/training/courses/${item.id}/view`, state: { item } });
            } else if (event === 'preview view') {
                dispatch(
                    AppActions.modalRequest({
                        type: 'Preview',
                        data: { before: { course: item, module: item.children[0], index: 0 } },
                    })
                );
            }
            setCourses(_courses);
        }
    };
    const handleSelectTags = (key) => (tags) => {
        const _seleted = clone(selected);
        _seleted[key] = tags;
        setSelected(_seleted);
    };
    const handleSubmit = (published) => () => {
        const _objectives = objectives.filter((e) => !!e);
        const _courses = courses.map((item) => ({
            alert_manager: null,
            blocked_by: item.blocked_by,
            card_type: 'course',
            card_template_id: item.id,
            complete_track: null,
        }));

        if (isEmpty(selected.author) || name === '') return;

        const payload = {
            track: {
                user_id: null,
                title: name,
                status: 0,
                data: {
                    cards: _courses,
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    objectives: _objectives,
                },
                result: null,
                deleted_at: null,
                author_id: selected.author[0],
                type: 1,
                completed_at: null,
                designation: 1,
                program_id: null,
                archived: 0,
                description,
                start_at: null,
                end_at: null,
                chronological_lock: chronologicalLock,
                time_lock: timeLock,
                program_level: 1,
                published,
            },
        };

        onUpdate(payload, [thumbnail]);
    };
    const handleObjectiveChange = (index) => (e) => {
        const _objectives = clone(objectives);
        _objectives[index] = e;
        setObjectives(_objectives);
    };
    const handleObjectiveAdd = () => {
        const _objectives = clone(objectives);
        if (_objectives.length <= '4') {
            _objectives.push('');
            setObjectives(_objectives);
        }
    };
    const handleObjectiveDelete = (index) => () => {
        const _objectives = clone(objectives);
        _objectives.splice(index, 1);
        setObjectives(_objectives);
    };

    const role = useSelector((state) => state.app.app_role_id);
    const authors = useSelector((state) => state.app.authors);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const categories = useSelector((state) => state.app.categories);
    const disabled = isEmpty(selected.author) || name === '';
    const disableDraft = !name || !description;

    const handleSelectMenu = () => (event, course) => {
        const quotaLen = length(courses);
        let clonedCourses = clone(courses);
        const indx = findIndex(propEq('id', course.id), clonedCourses);
        if (indx < 0) return;

        switch (event) {
            case 'move up':
                if (indx !== 0) clonedCourses = move(indx, indx - 1, modules);
                break;
            case 'move down':
                if (quotaLen !== indx + 1) clonedCourses = move(indx, indx + 1, modules);
                break;
            case 'remove':
                clonedCourses = remove(indx, 1, clonedCourses);
                break;
        }
        setCourses(clonedCourses);
    };

    const settings = [
        { name: 'Author', id: null, options: authors, select: 'author' },
        { name: 'Departments', id: null, options: departments, select: 'department' },
        { name: 'Competencies', id: null, options: competencies, select: 'competency' },
        { name: 'Categories', id: null, options: categories, select: 'category' },
    ];

    return (
        <>
            <Filter back="all tracks" />
            <div className="library-track-card border-5">
                <div className="detail-content pt-3">
                    <div className="d-flex jc-between mb-3">
                        <span className="dsl-b22 bold">Track</span>
                    </div>

                    <div className="d-flex jc-between ssm-column">
                        <div className="ssm-100 w-50 w-sm-100 mb-3">
                            <div className="pl-1 mb-3">
                                <Input className="course-input mb-3" title="Name" dataCy="title" value={name} maxLength={30} placeholder="Type here..." onChange={(e) => setName(e)} />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input
                                    className="course-text"
                                    title="Description"
                                    dataCy="description"
                                    placeholder="Type here..."
                                    as="textarea"
                                    rows="3"
                                    maxLength={100}
                                    value={description}
                                    onChange={(e) => setDescription(e)}
                                />
                            </div>
                            <div className="d-flex ai-start ps-1">
                                <p className="career-text core-input-label dsl-b12 pt-2 m-0">Learning Objectives</p>
                                <div className="d-flex flex-column w-100">
                                    {objectives.map((item, index) => (
                                        <Input
                                            className="course-input mb-2 d-flex-1"
                                            value={objectives[index]}
                                            placeholder="Type here..."
                                            direction="vertical"
                                            onChange={handleObjectiveChange(index)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="ssm-100 w-40 w-sm-100">
                            <Thumbnail type="upload" isDisablePicker source={thumbnail} size="medium" label="Upload image" onDrop={(e) => setThumbnail(e)} onDelete={() => setThumbnail(null)} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="library-track-card mt-2 pt-3 px-3 border-5" data-cy="includedCourse">
                <div className="modules-content pb-0" dataCy="courseLists">
                    <div className="d-flex">
                        <p className="dsl-b22 bold">Courses Attached</p>
                    </div>
                    <Course data={courses} role={role} onMenu={handleChildMenu} onSelect={handleSelectMenu()} edit />
                    <div className="d-h-start justify-content-end my-3 my-sm-0 pb-2 pt-4">
                        <Button className="ml-auto ps-0 d-flex" type="link" onClick={handleAttachCourse}>
                            <Icon name="fas fa-plus" size={14} color="#376caf" />
                            <span className="dsl-p14 text-400 ms-1">Attach Course</span>
                        </Button>
                    </div>
                </div>
            </div>
            <Settings isEditable expanded values={settings} handleSelect={handleSelectTags} />
            <div className="mt-3 d-flex jc-end">
                <Button className="mr-3" name="Draft" disabled={disableDraft} type="low" onClick={handleSubmit(0)} />
                <Button name="Publish" disabled={disabled} onClick={handleSubmit(1)} />
            </div>
        </>
    );
};

export default Add;
